import React from 'react';
import {
  createId,
  InputField,
  type InputFieldProps,
  useSlug,
} from '@bloomberg/media-ui-core';
import { Spinner } from '@bloomberg/media-ui-core';

import { SearchDropdown } from '../SearchDropdown/index';
import { SearchDropdownProps } from '../SearchDropdown/props';

import styles from './LineSearchDropdown.module.css';

export type Props = Omit<SearchDropdownProps, 'children'> & {
  size?: InputFieldProps['size'];
  variant?: InputFieldProps['variant'];
  isLoading?: boolean;
};

export const LineSearchDropdown = React.forwardRef<HTMLDivElement, Props>(
  function Component(props, ref) {
    const {
      disabled = false,
      name: userProvidedName,
      id: userProvidedId,
      placeholder = '',
      label = '',
      size = 'default',
      variant,
      onClear,
      unstyled,
      isLoading,
    } = props;

    const stableId = React.useRef(createId());

    // create a unique id for inputs
    const id = userProvidedId || stableId.current;

    const name = userProvidedName || 'LineSearchDropdown';
    // stable name for data-component attribute
    const [slugName] = useSlug(name);

    // main component return
    return (
      <SearchDropdown {...props} name={slugName} ref={ref}>
        {({ query, inputProps, getRootProps }) => (
          <InputField
            disableHelperText
            type="search"
            unstyled={unstyled}
            disabled={disabled || isLoading}
            label={label}
            id={id}
            size={size}
            name={`${name}-input`}
            placeholder={placeholder}
            value={query}
            onClear={onClear}
            variant={variant}
            inputProps={inputProps}
            {...getRootProps()}
            customRightIcon={
              isLoading && (
                <span className={styles.loading}>
                  <Spinner />
                </span>
              )
            }
          />
        )}
      </SearchDropdown>
    );
  }
);
